<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  Folder,
  File as FileIcon,
  ChevronDown,
  ChevronRight,
  Home,
  Download,
  Edit3,
  Trash2,
  RefreshCw,
  FileText,
  Film,
  Archive,
  Disc3,
  Package,
  Hash,
  AlertTriangle,
  Upload as UploadIcon,
  Magnet,
  FileDown,
  Link2,
  ShieldCheck,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Modal from '@/components/ui/Modal.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Badge from '@/components/ui/Badge.vue'
import ContextMenu, { type ContextMenuItem } from '@/components/ui/ContextMenu.vue'
import UploadModal from '@/components/settings/UploadModal.vue'
import {
  downloadFileUrl,
  fileTorrentUrl,
  fetchFileMagnet,
  deleteFilesBatch,
  listFiles,
  renameFile,
  adoptFile,
  type FileListEnvelope,
  type FileEntry,
  type NodeDeleteResult,
} from '@/api/files'
import { getClusterStatus } from '@/api/cluster'
import { useToast } from '@/composables/useToast'
import { copyToClipboard } from '@/composables/useClipboard'
import { useWebSocket } from '@/composables/useWebSocket'

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

const currentPath = ref('')

// v3.19.42: 文件列表双通道刷新 — 后端删除成功即推 file_change (WS), 此处
// invalidate 即时反映; refetchInterval 是断线兜底 (此前文件页是全站唯一
// 不轮询的页面, 跨节点删除后界面停在旧缓存直到手动刷新)。
const filesQuery = useQuery({
  queryKey: computed(() => ['files', currentPath.value]),
  queryFn: () => listFiles(currentPath.value),
  // v3.19.51: 后端截断 (5000 条) 的大目录全量 ReadDir+stat 实测 124ms/次 —
  // truncated 时轮询自动放宽到 30s; 常规目录保持 10s。
  refetchInterval: (query) => {
    const d = query.state.data as FileListEnvelope | undefined
    return d?.truncated ? 30_000 : 10_000
  },
})

// 本机 (或其他节点经 RPC 删到本机) 文件变更 → 立即重取列表, 不等 10s 轮询。
useWebSocket({
  url: () => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${window.location.host}/ws`
  },
  autoReconnect: true,
  stopOnSessionClear: true,
  onMessage: (msg) => {
    if (msg.type === 'file_change') {
      void qc.invalidateQueries({ queryKey: ['files'] })
    }
  },
})

interface Crumb {
  label: string
  path: string
}

const breadcrumbs = computed<Crumb[]>(() => {
  if (!currentPath.value) return []
  const parts = currentPath.value.split('/').filter(Boolean)
  const crumbs: Crumb[] = []
  let acc = ''
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p
    crumbs.push({ label: p, path: acc })
  }
  return crumbs
})

function goHome(): void {
  currentPath.value = ''
  clearSelection() // v3.13.79: 换目录清空勾选, 防止跨目录的隐形选中被误删
}

function enterDir(entry: FileEntry): void {
  if (!entry.is_dir) return
  currentPath.value = currentPath.value ? `${currentPath.value}/${entry.name}` : entry.name
  clearSelection()
}

function enterCrumb(crumb: Crumb): void {
  currentPath.value = crumb.path
  clearSelection()
}

const renaming = ref<FileEntry | null>(null)
const renameValue = ref('')
const showDelete = ref<FileEntry | null>(null)
const showUpload = ref(false)

// v3.13.78: 单行删除 = 全集群广播删除 (结果复用批量结果弹窗展示)
const deleteMutation = useMutation({
  mutationFn: (rel: string) => deleteFilesBatch([rel], 'cluster'),
  onSuccess: (resp) => {
    showDelete.value = null
    batchResults.value = resp.nodes ?? []
    batchPhase.value = 'result'
    showBatch.value = true
    void qc.invalidateQueries({ queryKey: ['files'] })
  },
})

const renameMutation = useMutation({
  mutationFn: ({ path, newName }: { path: string; newName: string }) => renameFile(path, newName),
  onSuccess: (resp) => {
    toast.success(t('settings.files.renamed', { from: resp.renamed, to: resp.to }))
    renaming.value = null
    void qc.invalidateQueries({ queryKey: ['files'] })
  },
})

function openRename(entry: FileEntry): void {
  renaming.value = entry
  renameValue.value = entry.name
}

function submitRename(): void {
  if (!renaming.value) return
  const newName = renameValue.value.trim()
  if (!newName || newName === renaming.value.name) {
    renaming.value = null
    return
  }
  renameMutation.mutate({ path: relativePath(renaming.value), newName })
}

function confirmDelete(entry: FileEntry): void {
  showDelete.value = entry
}

function doDelete(): void {
  if (!showDelete.value) return
  deleteMutation.mutate(relativePath(showDelete.value))
}

function relativePath(entry: FileEntry): string {
  return currentPath.value ? `${currentPath.value}/${entry.name}` : entry.name
}

// ── v3.13.72: 下载方式菜单 (直接下载 / 磁力链 / .torrent / 复制直链) ──

const shareMenu = ref<InstanceType<typeof ContextMenu> | null>(null)
const shareMenuItems = ref<ContextMenuItem[]>([])
// v3.13.74: 记相对路径而非对象引用 — vue-query 后台 refetch 会重建行对象,
// 按对象身份比较会让 toggle 失效
const shareMenuEntryRel = ref<string | null>(null)
// 磁力链生成中标记 (大目录首次哈希可达数十秒), 键为相对路径。
const magnetBusy = ref<Record<string, boolean>>({})
// v3.13.96: 外部文件"转内部"进行中标记 (全量哈希同级耗时), 键为相对路径。
const adoptBusy = ref<Record<string, boolean>>({})

function shareMenuFor(entry: FileEntry): ContextMenuItem[] {
  const rel = relativePath(entry)
  const busy = !!magnetBusy.value[rel]
  const adopting = !!adoptBusy.value[rel]
  const magnetItem: ContextMenuItem = {
    key: 'magnet',
    label: busy ? t('files.magnetGenerating') : t('files.dlMagnet'),
    icon: Magnet,
    disabled: busy,
  }
  const torrentItem: ContextMenuItem = { key: 'torrent', label: t('files.dlTorrent'), icon: FileDown }
  // v3.13.96: 外部文件 (未纳入集群指纹体系) 提供"转内部"; 目录恒提供 —
  // 后端列表不对目录打外部标 (内容混合无法判定), 递归采纳幂等可重复点。
  const adoptItem: ContextMenuItem = {
    key: 'adopt',
    label: adopting ? t('files.adopting') : t('files.adopt'),
    icon: ShieldCheck,
    disabled: busy || adopting,
  }
  if (entry.is_dir) {
    return [magnetItem, torrentItem, { ...adoptItem, divided: true }]
  }
  const items: ContextMenuItem[] = [
    { key: 'direct', label: t('files.dlDirect'), icon: Download },
    { ...magnetItem, divided: true },
    torrentItem,
    { key: 'copy-link', label: t('files.dlCopyLink'), icon: Link2, divided: true },
  ]
  if (entry.external) items.push(adoptItem)
  return items
}

function openShareMenu(e: MouseEvent, entry: FileEntry): void {
  const rel = relativePath(entry)
  // v3.13.73/74: 再次点击同一行的按钮 = 关闭 (toggle)
  if (shareMenu.value?.isOpen() && shareMenuEntryRel.value === rel) {
    shareMenu.value.close()
    return
  }
  shareMenuEntryRel.value = rel
  shareMenuItems.value = shareMenuFor(entry)
  shareMenu.value?.open(e, shareMenuItems.value)
}

function triggerBrowserDownload(url: string, filename: string): void {
  // 临时 <a download> 保持"另存为"语义 (服务端部分 MIME 会内联打开)。
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function copyMagnet(rel: string): Promise<void> {
  magnetBusy.value = { ...magnetBusy.value, [rel]: true }
  try {
    const info = await fetchFileMagnet(rel)
    if (await copyToClipboard(info.magnet)) {
      toast.success(t('files.magnetCopied', { name: info.name }), t('files.magnetCopiedDesc'))
    } else {
      toast.warning(t('files.magnetCopied', { name: info.name }), info.magnet)
    }
  } catch (err) {
    toast.error(t('files.magnetFailed'), err instanceof Error ? err.message : undefined)
  } finally {
    const next = { ...magnetBusy.value }
    delete next[rel]
    magnetBusy.value = next
  }
}

async function copyDirectLink(rel: string): Promise<void> {
  const abs = new URL(downloadFileUrl(rel), window.location.origin).href
  if (await copyToClipboard(abs)) {
    toast.success(t('files.linkCopied'), abs)
  }
}

// v3.13.96: 外部文件转内部 — 哈希+注册+广播进集群指纹体系, 其它节点即可
// 按需拉取 (懒分发)。耗时与磁力生成同级 (全量 SHA-256), busy 态防重复触发。
async function adoptExternal(rel: string): Promise<void> {
  if (adoptBusy.value[rel]) return
  adoptBusy.value = { ...adoptBusy.value, [rel]: true }
  try {
    const resp = await adoptFile(rel)
    if (resp.failed > 0) {
      toast.warning(
        t('files.adoptPartial', { adopted: resp.adopted, failed: resp.failed }),
        t('files.adoptLocalOnly'),
      )
    } else {
      const n = resp.is_dir ? resp.adopted : 1
      toast.success(t('files.adoptSuccess', { n }), t('files.adoptSuccessDesc'))
    }
    // 徽标状态来自列表的 external 字段, 采纳成功后刷新即消失。
    void qc.invalidateQueries({ queryKey: ['files'] })
  } catch (err) {
    toast.error(t('files.adoptFailed'), err instanceof Error ? err.message : undefined)
    // v3.13.97 (A7): 客户端 120s 超时只中止等待, 服务端哈希可能仍会完成 —
    // 刷新列表让徽标与真实注册状态对齐。
    void qc.invalidateQueries({ queryKey: ['files'] })
  } finally {
    const next = { ...adoptBusy.value }
    delete next[rel]
    adoptBusy.value = next
  }
}

async function onShareSelect(key: string): Promise<void> {
  const rel = shareMenuEntryRel.value
  if (!rel) return
  // v3.13.74: 文件名从 rel 推导, 不再持有行对象 (refetch 后引用会失效)
  const name = rel.split('/').pop() ?? rel
  switch (key) {
    case 'direct':
      triggerBrowserDownload(downloadFileUrl(rel), name)
      break
    case 'magnet':
      await copyMagnet(rel)
      break
    case 'torrent':
      triggerBrowserDownload(fileTorrentUrl(rel), `${name}.torrent`)
      break
    case 'copy-link':
      await copyDirectLink(rel)
      break
    case 'adopt':
      await adoptExternal(rel)
      break
  }
}

function formatBytes(n: number): string {
  // v3.13.44: 0 字节文件显示"空", 不再显示占位符 "—"
  if (n <= 0) return t('files.emptyMark')
  const KiB = 1024
  const MiB = 1024 * KiB
  const GiB = 1024 * MiB
  const TiB = 1024 * GiB
  if (n >= TiB) return `${(n / TiB).toFixed(2)} TiB`
  if (n >= GiB) return `${(n / GiB).toFixed(2)} GiB`
  if (n >= MiB) return `${(n / MiB).toFixed(2)} MiB`
  if (n >= KiB) return `${(n / KiB).toFixed(2)} KiB`
  return `${n} B`
}

function iconFor(entry: FileEntry): typeof FileIcon {
  if (entry.is_dir) return Folder
  switch (entry.type) {
    case '视频': return Film
    case '文档': return FileText
    case '压缩包': return Archive
    case '镜像': return Disc3
    case '安装包': return Package
    case '种子': return Hash
    default: return FileIcon
  }
}

function refresh(): void {
  void qc.invalidateQueries({ queryKey: ['files'] })
}

// Sort: dirs first, then by name
const sortedEntries = computed(() => {
  const list = filesQuery.data.value?.entries ?? []
  return [...list].sort((a, b) => {
    if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1
    return a.name.localeCompare(b.name, undefined, { numeric: true })
  })
})

// ── v3.13.80: 搜索 / 类型筛选 / 排序 ──

const searchQuery = ref('')
// v3.14.0 (T3): 搜索 debounce 200ms — 直连 computed 会让每个键入触发
// 全列表 filter+sort+虚拟重算 (5000 条封顶目录下每键一次)。
const searchInput = ref('')
let searchDebounce: number | null = null
watch(searchInput, (v) => {
  if (searchDebounce !== null) window.clearTimeout(searchDebounce)
  searchDebounce = window.setTimeout(() => {
    searchQuery.value = v
    searchDebounce = null
  }, 200)
})
const typeFilter = ref('全部')
const sortBy = ref<'name' | 'size' | 'time'>('name')
const sortAsc = ref(true)

// v3.18.0 修复: filteredEntries 必须在 useVirtualizer 之前声明 —
// 原来声明在 line 408 (sortedEntries 的搜索/排序依赖之后), 但
// useVirtualizer 在 line 341 就急切求值引用了它 → TDZ
// ("Cannot access 'C' before initialization", /files 页面崩溃)。
const filteredEntries = computed(() => {
  let list = sortedEntries.value
  const q = searchQuery.value.trim().toLowerCase()
  if (q) list = list.filter((e) => e.name.toLowerCase().includes(q))
  if (typeFilter.value !== '全部') {
    list = list.filter((e) => (e.is_dir ? typeFilter.value === '目录' : e.type === typeFilter.value))
  }
  const dir = sortAsc.value ? 1 : -1
  // v3.14.0 (T2): 次键 = name — size/time 相等时 (同秒上传、0 字节文件组)
  // 无次键会导致列表在两次渲染间抖动 (Array.sort 不稳定由引擎决定)。
  return [...list].sort((a, b) => {
    if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1
    let cmp = 0
    switch (sortBy.value) {
      case 'size':
        cmp = a.size - b.size
        break
      case 'time':
        // "2006-01-02 15:04" 定宽格式, 字典序即时间序
        cmp = a.mod_time > b.mod_time ? 1 : a.mod_time < b.mod_time ? -1 : 0
        break
      default:
        cmp = a.name.localeCompare(b.name, undefined, { numeric: true })
    }
    if (cmp !== 0) return cmp * dir
    return a.name.localeCompare(b.name, undefined, { numeric: true })
  })
})

// ── v3.14.0: 大目录虚拟滚动 (对齐 TasksView 的 @tanstack/vue-virtual 模式) ──
// 后端已封顶 5000 条, 但全量渲染 5000 行仍有可感知的首帧卡顿; 阈值之上
// 切换为虚拟化渲染 (行高估算 44px = py-2.5 + text-sm), 之下保持普通渲染。
import { useVirtualizer } from '@tanstack/vue-virtual'
const FILES_VIRTUAL_THRESHOLD = 200
const FILES_ROW_HEIGHT = 44
const filesScrollEl = ref<HTMLDivElement | null>(null)
const useFilesVirtual = computed(() => filteredEntries.value.length >= FILES_VIRTUAL_THRESHOLD)
const filesVirtualizer = useVirtualizer(
  computed(() => ({
    count: filteredEntries.value.length,
    getScrollElement: () => filesScrollEl.value,
    estimateSize: () => FILES_ROW_HEIGHT,
    overscan: 8,
  })),
)
const filesVirtualHeight = computed(() =>
  useFilesVirtual.value ? filesVirtualizer.value.getTotalSize() : 0,
)
// 统一的渲染源: 非虚拟 = 全量 (offset 仅占位), 虚拟 = 可见窗口行 (带偏移)。
const visibleRows = computed(() => {
  if (!useFilesVirtual.value) {
    return filteredEntries.value.map((entry, i) => ({ entry, offset: i * FILES_ROW_HEIGHT }))
  }
  return filesVirtualizer.value
    .getVirtualItems()
    .map((r) => ({ entry: filteredEntries.value[r.index], offset: r.start }))
    .filter((r) => r.entry != null)
})

// 后端 fileType() 的分类值恒为中文 (与 UI 语言无关), 筛选按原值比对
const TYPE_FILTERS = ['全部', '目录', '视频', '文档', '压缩包', '镜像', '安装包', '种子', '其他'] as const

function typeLabel(ty: string): string {
  if (ty === '全部') return t('files.filterAll')
  if (ty === '目录') return t('files.typeDir')
  const keyMap: Record<string, string> = {
    视频: 'files.typeVideo',
    文档: 'files.typeDoc',
    压缩包: 'files.typeArchive',
    镜像: 'files.typeImage',
    安装包: 'files.typeInstaller',
    种子: 'files.typeTorrent',
    其他: 'files.typeOther',
  }
  return keyMap[ty] ? t(keyMap[ty]) : ty
}

function sortLabel(key: 'name' | 'size' | 'time'): string {
  const m: Record<string, string> = {
    name: t('files.sortName'),
    size: t('files.sortSize'),
    time: t('files.sortTime'),
  }
  return m[key]
}

function toggleSort(key: 'name' | 'size' | 'time'): void {
  if (sortBy.value === key) {
    sortAsc.value = !sortAsc.value
  } else {
    sortBy.value = key
    sortAsc.value = true
  }
}

// ── v3.13.76: 勾选 + 跨节点批量删除 ──

const selected = ref<Set<string>>(new Set())
const selectedCount = computed(() => selected.value.size)

function toggleSelect(rel: string): void {
  const next = new Set(selected.value)
  if (next.has(rel)) next.delete(rel)
  else next.add(rel)
  selected.value = next
}

const allSelected = computed(
  () =>
    filteredEntries.value.length > 0 &&
    filteredEntries.value.every((e) => selected.value.has(relativePath(e))),
)
const someSelected = computed(() =>
  filteredEntries.value.some((e) => selected.value.has(relativePath(e))),
)

// 全选只作用于当前筛选后的可见项
function toggleSelectAll(): void {
  selected.value = allSelected.value
    ? new Set<string>()
    : new Set(filteredEntries.value.map((e) => relativePath(e)))
}

function clearSelection(): void {
  selected.value = new Set()
}

const showBatch = ref(false)
const batchPhase = ref<'confirm' | 'result'>('confirm')
const batchResults = ref<NodeDeleteResult[]>([])

// 集群节点列表 (删除弹窗打开时才拉, 失败静默 — 预览不可用时删除仍广播)
const clusterQuery = useQuery({
  queryKey: ['cluster', 'status'],
  queryFn: getClusterStatus,
  enabled: computed(() => showBatch.value || !!showDelete.value),
  retry: false,
  staleTime: 30_000,
})
const clusterNodes = computed(() => clusterQuery.data.value?.nodes ?? [])
const clusterAvailable = computed(() => clusterNodes.value.length > 0)
const localNodeName = computed(() => {
  const local = clusterNodes.value.find((n) => n.is_local)
  return local?.name || t('files.thisNode')
})

const batchNodePreview = computed(() => {
  return clusterNodes.value.map((n) => {
    const mode = n.mode || 'both'
    const name = (n.name || n.id) + (n.is_local ? ` · ${t('files.thisNode')}` : '')
    if (!n.is_local && (n.status ?? '') !== 'online') {
      return { id: n.id, name, icon: '✗', label: t('files.offline'), cls: 'text-fg-subtle' }
    }
    if (mode === 'upload_only') {
      return { id: n.id, name, icon: '⊘', label: t('files.skipUploadOnly'), cls: 'text-amber-500' }
    }
    if (mode === 'download_only') {
      return { id: n.id, name, icon: '⊘', label: t('files.skipDownloadOnly'), cls: 'text-amber-500' }
    }
    return { id: n.id, name, icon: '✓', label: t('files.willDelete'), cls: 'text-emerald-500' }
  })
})

const batchDeleteMutation = useMutation({
  mutationFn: () => deleteFilesBatch([...selected.value], 'cluster'),
  onSuccess: (resp) => {
    batchPhase.value = 'result'
    batchResults.value = resp.nodes ?? []
  },
  onError: (err) => {
    toast.error(t('files.batchFailed'), err instanceof Error ? err.message : undefined)
  },
})

const batchSummary = computed(() => {
  const deleted = batchResults.value.filter((n) => n.status === 'deleted').length
  const skipped = batchResults.value.filter((n) => n.status === 'skipped').length
  const failed = batchResults.value.filter((n) => n.status === 'failed').length
  return t('files.batchResultSummary', { deleted, skipped, failed })
})

function openBatchDelete(): void {
  batchPhase.value = 'confirm'
  batchResults.value = []
  showBatch.value = true
}

function confirmBatchDelete(): void {
  batchDeleteMutation.mutate()
}

function closeBatch(): void {
  showBatch.value = false
}

// 关闭时收尾: 已执行过删除 → 清空勾选 + 刷新列表
watch(showBatch, (open) => {
  if (open) return
  if (batchPhase.value === 'result') {
    clearSelection()
    refresh()
  }
  batchDeleteMutation.reset()
  batchPhase.value = 'confirm'
})

function nodeStatusLabel(n: NodeDeleteResult): string {
  if (n.status === 'deleted') return t('files.nodeDeleted')
  if (n.status === 'skipped') {
    if (n.reason === 'upload_only') return t('files.skipUploadOnly')
    if (n.reason === 'download_only') return t('files.skipDownloadOnly')
    if (n.reason === 'offline') return t('files.offline')
    return t('files.nodeSkipped')
  }
  return t('files.nodeFailed')
}
</script>

<template>
  <div class="space-y-6">
    <Card :title="t('files.title')" :subtitle="t('files.subtitle')">
      <template #header>
        <div class="flex items-center gap-2">
          <Button variant="primary" size="sm" @click="showUpload = true">
            <template #icon><UploadIcon :size="14" /></template>
            {{ t('files.upload') }}
          </Button>
          <Button variant="outline" size="sm" :loading="filesQuery.isFetching.value" @click="refresh">
            <template #icon><RefreshCw :size="14" /></template>
            {{ t('common.refresh') }}
          </Button>
        </div>
      </template>

      <!-- Breadcrumb -->
      <nav class="mb-4 flex flex-wrap items-center gap-1 text-sm" aria-label="breadcrumb">
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          @click="goHome"
        >
          <Home :size="13" />
          <span>{{ t('settings.files.root') }}</span>
        </button>
        <template v-for="(crumb, i) in breadcrumbs" :key="crumb.path">
          <ChevronRight :size="13" class="text-fg-subtle" />
          <button
            type="button"
            class="rounded px-1.5 py-0.5 hover:bg-bg-hover"
            :class="i === breadcrumbs.length - 1 ? 'font-medium text-fg' : 'text-fg-muted hover:text-fg'"
            @click="enterCrumb(crumb)"
          >
            {{ crumb.label }}
          </button>
        </template>
      </nav>

      <!-- Body -->
      <div v-if="filesQuery.isLoading.value" class="flex items-center justify-center py-12">
        <Spinner :size="20" :label="t('common.loading')" />
      </div>

      <!-- v3.13.76: 勾选后浮出的批量操作条 — v3.13.77 fix: 必须用独立 v-if
           与列表共存, 之前误写成 v-else-if 挂进互斥渲染链, 勾选后列表被顶掉 -->
      <div
        v-if="!filesQuery.isLoading.value && selectedCount > 0"
        class="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-bg-surface px-4 py-2"
      >
        <span class="text-sm text-fg">{{ t('files.selectedCount', { n: selectedCount }) }}</span>
        <div class="flex-1" />
        <Button variant="danger" size="sm" @click="openBatchDelete">
          <template #icon><Trash2 :size="14" /></template>
          {{ t('files.batchDelete') }}
        </Button>
        <Button variant="ghost" size="sm" @click="clearSelection">
          {{ t('files.clearSelection') }}
        </Button>
      </div>

      <!-- v3.13.80: 搜索 / 类型筛选 / 排序工具栏 -->
      <div
        v-if="!filesQuery.isLoading.value && sortedEntries.length > 0"
        class="mb-3 flex flex-wrap items-center gap-2"
      >
        <div class="relative">
          <input
            v-model="searchInput"
            type="search"
            class="h-8 w-48 rounded-md border border-border bg-bg-surface px-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
            :placeholder="t('files.searchPlaceholder')"
            :aria-label="t('files.searchPlaceholder')"
          />
        </div>
        <div class="flex flex-wrap items-center gap-1">
          <button
            v-for="ty in TYPE_FILTERS"
            :key="ty"
            type="button"
            class="rounded-full px-2.5 py-1 text-xs transition-colors"
            :class="
              typeFilter === ty
                ? 'bg-brand-500 text-white'
                : 'bg-bg-hover text-fg-muted hover:text-fg'
            "
            @click="typeFilter = ty"
          >
            {{ typeLabel(ty) }}
          </button>
        </div>
        <div class="ml-auto flex items-center gap-1 text-xs">
          <span class="text-fg-subtle">{{ t('files.sortBy') }}</span>
          <button
            v-for="key in (['name', 'size', 'time'] as const)"
            :key="key"
            type="button"
            class="rounded px-1.5 py-0.5 transition-colors"
            :class="sortBy === key ? 'bg-bg-active font-medium text-fg' : 'text-fg-muted hover:text-fg'"
            @click="toggleSort(key)"
          >
            {{ sortLabel(key) }}
            <span v-if="sortBy === key">{{ sortAsc ? '↑' : '↓' }}</span>
          </button>
        </div>
      </div>

      <!-- v3.13.44: 空目录只显示"空", 不再渲染大占位符 (图标+提示文案) -->
      <div
        v-if="!filesQuery.isLoading.value && sortedEntries.length === 0"
        class="py-12 text-center text-sm text-fg-subtle"
        aria-live="polite"
      >
        {{ t('files.emptyMark') }}
      </div>

      <!-- v3.13.80: 有文件但筛选/搜索无匹配 -->
      <div
        v-else-if="filteredEntries.length === 0"
        class="py-12 text-center text-sm text-fg-subtle"
        aria-live="polite"
      >
        {{ t('files.noMatch') }}
      </div>

      <template v-else-if="!filesQuery.isLoading.value && filteredEntries.length > 0">
        <!-- v3.13.76: 全选行 -->
        <div class="mb-2 flex items-center gap-3 px-1">
          <input
            type="checkbox"
            class="h-4 w-4 accent-brand-500"
            :checked="allSelected"
            :indeterminate.prop="!allSelected && someSelected"
            :aria-label="t('files.selectAll')"
            @change="toggleSelectAll"
          />
          <span class="text-xs text-fg-muted">{{ t('files.selectAll') }}</span>
          <span v-if="selectedCount > 0" class="text-xs text-fg-subtle">
            {{ t('files.selectedCount', { n: selectedCount }) }}
          </span>
        </div>

        <!-- v3.19.53: 大目录截断提示 — v3.14.0 起后端 5000 条封顶并返回
             truncated 标记, 此前兼容层把它丢了, 提示从未显示过 -->
        <div
          v-if="filesQuery.data.value?.truncated"
          class="mb-2 flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-600 dark:text-amber-400"
        >
          <AlertTriangle :size="13" class="shrink-0" />
          {{ t('files.truncatedNotice', { total: filesQuery.data.value.total }) }}
        </div>

        <!-- v3.14.0: 大目录 (≥200 行) 切换虚拟滚动 — 外层限高滚动容器 +
             相对定位画布, 行按 offset 绝对定位; 普通规模保持原渲染。 -->
        <div
          :ref="useFilesVirtual ? (el) => { filesScrollEl = el as HTMLDivElement | null } : undefined"
          :class="useFilesVirtual ? 'max-h-[70vh] overflow-y-auto' : ''"
        >
          <ul
            class="divide-y divide-border rounded-md border border-border"
            :style="useFilesVirtual ? { height: `${filesVirtualHeight}px`, position: 'relative' } : {}"
          >
            <li
              v-for="{ entry, offset } in visibleRows"
              :key="entry.name"
              class="group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-bg-hover"
              :style="useFilesVirtual ? { position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${offset}px)` } : {}"
            >
            <input
              type="checkbox"
              class="h-4 w-4 shrink-0 accent-brand-500"
              :checked="selected.has(relativePath(entry))"
              :aria-label="t('files.select')"
              @change="toggleSelect(relativePath(entry))"
            />
            <component
              :is="iconFor(entry)"
              :size="16"
              :class="entry.is_dir ? 'text-brand-500' : 'text-fg-muted'"
            />
          <button
            v-if="entry.is_dir"
            type="button"
            class="flex-1 min-w-0 truncate text-left text-sm font-medium text-fg hover:text-brand-600 dark:hover:text-brand-300"
            @click="enterDir(entry)"
          >
            {{ entry.name }}
          </button>
          <template v-else>
            <span class="min-w-0 flex-1 truncate text-sm text-fg">{{ entry.name }}</span>
            <!-- v3.13.96: 外部文件徽标 — 未纳入集群指纹体系 (手动移入/BT 下载),
                 仅本机可用; 下载方式菜单里可"转为内部文件"纳入集群分发。 -->
            <Badge
              v-if="entry.external"
              variant="warning"
              class="ml-1.5 hidden shrink-0 sm:inline-flex"
              :title="t('files.adoptLocalOnly')"
            >
              {{ t('files.external') }}
            </Badge>
          </template>

          <span class="hidden w-28 text-right text-xs text-fg-muted sm:inline">
            {{ entry.is_dir ? '—' : formatBytes(entry.size) }}
          </span>
          <span class="hidden w-32 text-right text-xs text-fg-muted sm:inline">{{ entry.mod_time }}</span>

          <div class="flex items-center gap-1">
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-0.5 rounded px-1.5 text-fg-muted hover:bg-bg-active hover:text-fg"
              :aria-label="t('files.dlMenu')"
              :title="t('files.dlMenu')"
              @click="openShareMenu($event, entry)"
            >
              <Download :size="14" />
              <RefreshCw
                v-if="magnetBusy[relativePath(entry)] || adoptBusy[relativePath(entry)]"
                :size="10"
                class="animate-spin"
              />
              <ChevronDown v-else :size="10" />
            </button>
            <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded text-fg-muted hover:bg-bg-active hover:text-fg"
              :title="t('files.rename')"
              :aria-label="t('files.rename')"
              @click="openRename(entry)"
            >
              <Edit3 :size="14" />
            </button>
            <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded text-fg-muted hover:bg-danger/10 hover:text-danger"
              :title="t('files.delete')"
              :aria-label="t('files.delete')"
              @click="confirmDelete(entry)"
            >
              <Trash2 :size="14" />
            </button>
          </div>
        </li>
        </ul>
      </div>
      </template>
    </Card>

    <!-- Rename modal -->
    <Modal v-model="renaming" :title="t('files.rename')" size="sm">
      <template v-if="renaming">
        <p class="mb-3 text-xs text-fg-muted">
          {{ t('settings.files.renameHint', { name: renaming.name }) }}
        </p>
        <input
          v-model="renameValue"
          class="w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
          :placeholder="renaming.name"
          @keydown.enter="submitRename"
        />
      </template>
      <template #footer>
        <Button variant="ghost" size="sm" @click="renaming = null">{{ t('common.cancel') }}</Button>
        <Button variant="primary" size="sm" :loading="renameMutation.isPending.value" @click="submitRename">
          {{ t('common.save') }}
        </Button>
      </template>
    </Modal>

    <!-- Delete confirm -->
    <Modal v-model="showDelete" :title="t('files.delete')" size="sm">
      <div v-if="showDelete" class="flex items-start gap-3">
        <div class="rounded-md bg-danger/15 p-2 text-danger shrink-0">
          <AlertTriangle :size="18" />
        </div>
        <p class="text-sm text-fg-muted">
          {{ t('files.syncDeleteHint', { name: showDelete.name }) }}
          <span v-if="showDelete.is_dir" class="mt-1 block text-xs text-fg-subtle">
            {{ t('settings.files.deleteDirHint') }}
          </span>
        </p>
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showDelete = null">{{ t('common.cancel') }}</Button>
        <Button variant="danger" size="sm" :loading="deleteMutation.isPending.value" @click="doDelete">
          {{ t('files.deleteAllNodes') }}
        </Button>
      </template>
    </Modal>

    <!-- Upload modal -->
    <UploadModal
      v-model="showUpload"
      :default-path="currentPath"
      @uploaded="refresh"
    />

    <!-- v3.13.78: 批量删除 = 广播所有节点 (节点预览 + 结果) -->
    <Modal v-model="showBatch" :title="t('files.batchDelete')" size="md">
      <template v-if="batchPhase === 'confirm'">
        <p class="mb-4 text-sm text-fg-muted">{{ t('files.batchDeleteHint', { n: selectedCount }) }}</p>
        <p v-if="!clusterAvailable" class="mb-4 text-xs text-amber-500">
          {{ t('files.clusterUnavailable') }}
        </p>
        <div
          v-if="clusterAvailable"
          class="max-h-48 space-y-1.5 overflow-auto rounded-md border border-border p-2"
        >
          <div
            v-for="n in batchNodePreview"
            :key="n.id"
            class="flex items-center gap-2 text-sm"
          >
            <span class="w-4 text-center">{{ n.icon }}</span>
            <span class="min-w-0 flex-1 truncate text-fg">{{ n.name }}</span>
            <span class="text-xs" :class="n.cls">{{ n.label }}</span>
          </div>
          <p class="pt-1 text-xs text-fg-subtle">{{ t('files.skipHint') }}</p>
        </div>
      </template>
      <template v-else>
        <div class="space-y-2">
          <div
            v-for="n in batchResults"
            :key="n.id"
            class="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span class="w-4 text-center">
              {{ n.status === 'deleted' ? '✓' : n.status === 'skipped' ? '⊘' : '✗' }}
            </span>
            <span class="min-w-0 flex-1 truncate text-fg">
              {{ n.name || n.id }}
              <span v-if="n.is_local" class="text-fg-subtle"> · {{ t('files.thisNode') }}</span>
            </span>
            <span
              class="text-xs"
              :class="
                n.status === 'deleted'
                  ? 'text-emerald-500'
                  : n.status === 'failed'
                    ? 'text-danger'
                    : 'text-amber-500'
              "
            >
              {{ nodeStatusLabel(n) }}
              <span v-if="n.status === 'deleted'"> · {{ t('files.filesUnit', { n: n.files }) }}</span>
            </span>
          </div>
        </div>
        <p class="mt-3 text-xs text-fg-muted">{{ batchSummary }}</p>
      </template>
      <template #footer>
        <template v-if="batchPhase === 'confirm'">
          <Button variant="ghost" size="sm" @click="closeBatch">{{ t('common.cancel') }}</Button>
          <Button
            variant="danger"
            size="sm"
            :loading="batchDeleteMutation.isPending.value"
            @click="confirmBatchDelete"
          >
            {{ t('common.delete') }}
          </Button>
        </template>
        <template v-else>
          <Button variant="primary" size="sm" @click="closeBatch">{{ t('files.done') }}</Button>
        </template>
      </template>
    </Modal>

    <!-- 下载方式菜单 (v3.13.72): 直接下载/磁力链/.torrent/复制直链 -->
    <ContextMenu ref="shareMenu" :items="shareMenuItems" @select="onShareSelect" />
  </div>
</template>
